import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { OtherRankInput, isOther, otherText, selValue } from "@/lib/ranks";
import { Plus, Loader2, ClipboardList, AlarmClock } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { usePipelineMeta } from "@/lib/pipeline";

export const REQ_STATUS = { open: "Open", filled: "Filled", cancelled: "Cancelled" };
const REQ_CLS = { open: "bg-sky-50 text-sky-700", filled: "bg-emerald-50 text-emerald-700", cancelled: "bg-slate-100 text-slate-500" };
export const ReqBadge = ({ s, testid }) => <span data-testid={testid} className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${REQ_CLS[s]}`}>{REQ_STATUS[s]}</span>;

function Pick({ label, value, onChange, options, testid, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={selValue(value)} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testid}-option-${l}`}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <OtherRankInput value={value} onChange={onChange} testid={`${testid}-other-input`} />
    </div>
  );
}

function CreateDialog({ open, onOpenChange, meta, onSaved }) {
  const [vessels, setVessels] = useState([]);
  const [f, setF] = useState({ vessel_id: "", rank: "", required_count: 1, joining_date: "", joining_port: "", requirement_status: "open" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) api.get("/vessels").then(({ data }) => setVessels(data)).catch(() => {}); }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.vessel_id || !f.rank) { toast.error("Choose a vessel and rank"); return; }
    if (isOther(f.rank) && otherText(f.rank).trim().length < 2) { toast.error("Type the custom rank name for Others (at least 2 characters)"); return; }
    setSaving(true);
    try { await api.post("/pipeline/requirements", { ...f, required_count: Number(f.required_count) }); toast.success("Requirement created"); onOpenChange(false); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="requirement-create-dialog">
        <DialogHeader><DialogTitle>New crew requirement</DialogTitle><DialogDescription>Each rank can be listed once per vessel.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Pick label="Vessel" value={f.vessel_id} onChange={(v) => setF({ ...f, vessel_id: v })} options={vessels.map((v) => [v.id, v.vessel_name])} testid="req-vessel-select" placeholder="Select vessel" />
          <Pick label="Rank required" value={f.rank} onChange={(v) => setF({ ...f, rank: v })} options={(meta?.ranks || []).map((r) => [r, r])} testid="req-rank-select" placeholder="Select rank" />
          <div className="space-y-1.5"><Label htmlFor="rq-qty">Quantity</Label><Input id="rq-qty" type="number" min={1} value={f.required_count} onChange={(e) => setF({ ...f, required_count: e.target.value })} data-testid="req-qty-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="rq-date">Joining date</Label><Input id="rq-date" type="date" value={f.joining_date} onChange={(e) => setF({ ...f, joining_date: e.target.value })} data-testid="req-joining-date-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="rq-port">Joining port</Label><Input id="rq-port" value={f.joining_port} onChange={(e) => setF({ ...f, joining_port: e.target.value })} data-testid="req-joining-port-input" /></div>
          <Pick label="Status" value={f.requirement_status} onChange={(v) => setF({ ...f, requirement_status: v })} options={Object.entries(REQ_STATUS)} testid="req-status-select" />
          <DialogFooter className="sm:col-span-2"><Button type="submit" disabled={saving} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="req-create-submit-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create requirement"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const istToday = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);
export const urgency = (r) => {
  if (r.requirement_status !== "open" || !r.joining_date) return null;
  const days = Math.round((new Date(r.joining_date) - new Date(istToday())) / 864e5);
  return days < 0 ? { label: "Overdue", days } : days <= 14 ? { label: "Urgent", days } : null;
};
const UrgentBadge = ({ u, testid }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded ${u.label === "Overdue" ? "bg-rose-600 text-white" : "bg-orange-500 text-white"}`} data-testid={testid}>
    <AlarmClock className="w-3 h-3" />{u.label}{u.days >= 0 ? ` · ${u.days === 0 ? "today" : `${u.days}d`}` : ""}
  </span>
);

function ReqRow({ r, first, navigate }) {
  const u = urgency(r);
  return (
    <tr onClick={() => navigate(`/requirements/${r.id}`)} className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${u ? "bg-orange-50/60 border-l-4 border-l-orange-500" : ""}`} data-testid={`pipeline-req-row-${r.id}`}>
      <td className="px-4 py-3 text-slate-800"><div className="flex flex-wrap items-center gap-1.5">{first}{u && <UrgentBadge u={u} testid={`req-urgent-${r.id}`} />}</div></td>
      <td className="px-4 py-3 font-mono">{r.required_count}</td>
      <td className={`px-4 py-3 font-mono text-xs hidden md:table-cell ${u ? "text-orange-700 font-semibold" : ""}`}>{r.joining_date || "—"}</td>
      <td className="px-4 py-3 hidden md:table-cell">{r.joining_port || "—"}</td>
      <td className="px-4 py-3 font-mono hidden md:table-cell">{r.candidates_assigned}</td>
      <td className="px-4 py-3"><ReqBadge s={r.requirement_status} testid={`pipeline-req-status-${r.id}`} /></td>
    </tr>
  );
}

function GroupedTable({ items, view, navigate }) {
  const key = view === "vessel" ? "vessel_name" : "rank";
  const groups = {};
  items.forEach((r) => { (groups[r[key]] ||= []).push(r); });
  const names = Object.keys(groups).sort((a, b) => a.localeCompare(b));
  return (
    <div className="space-y-4" data-testid={`requirements-${view}-view`}>
      {names.map((n) => {
        const rows = [...groups[n]].sort((a, b) => (urgency(b) ? 1 : 0) - (urgency(a) ? 1 : 0) || (a.joining_date || "9").localeCompare(b.joining_date || "9"));
        const nUrgent = rows.filter(urgency).length;
        const open = rows.filter((r) => r.requirement_status === "open").reduce((s, r) => s + (r.required_count || 0), 0);
        const slug = n.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
        return (
          <div key={n} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`req-group-${slug}`}>
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-semibold text-slate-900">{n}</div>
                {view === "vessel" && rows[0].vessel_type && <div className="text-xs text-cyan-700">{rows[0].vessel_type}</div>}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
              {nUrgent > 0 && <span className="text-xs font-semibold text-white bg-orange-500 rounded-full px-2 py-0.5" data-testid={`req-group-urgent-${slug}`}>{nUrgent} urgent</span>}
              <span className="text-xs font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5" data-testid={`req-group-open-${slug}`}>
                {open} open {view === "vessel" ? "crew slot" : "position"}{open === 1 ? "" : "s"}{view === "rank" ? ` · ${new Set(rows.map((r) => r.vessel_name)).size} vessel${new Set(rows.map((r) => r.vessel_name)).size === 1 ? "" : "s"}` : ""}
              </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-fixed min-w-[360px]">
                <colgroup><col className="w-[34%]" /><col className="w-[10%]" /><col className="hidden md:table-column w-[16%]" /><col className="hidden md:table-column w-[16%]" /><col className="hidden md:table-column w-[10%]" /><col className="w-[14%]" /></colgroup>
                <thead className="text-xs uppercase tracking-wider text-slate-500">
                  <tr>{[[view === "vessel" ? "Rank" : "Vessel", 1], ["Qty", 1], ["Joining"], ["Port"], ["Assigned"], ["Status", 1]].map(([h, a]) => <th key={h} className={`px-4 py-2 text-left font-medium ${a ? "" : "hidden md:table-cell"}`}>{h}</th>)}</tr>
                </thead>
                <tbody>{rows.map((r) => <ReqRow key={r.id} r={r} navigate={navigate} first={view === "vessel" ? r.rank : r.vessel_name} />)}</tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function RequirementsPage() {
  const meta = usePipelineMeta();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("vessel");
  const [params, setParams] = useSearchParams();
  const urgentOnly = params.get("filter") === "urgent";
  const shown = items && urgentOnly ? items.filter(urgency) : items;
  const load = () => api.get("/pipeline/requirements", { params: status === "all" ? {} : { status } }).then(({ data }) => setItems(data)).catch((err) => toast.error(formatApiError(err)));
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  return (
    <div className="space-y-6" data-testid="requirements-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Pipeline</div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>Crew Requirements</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="req-view-toggle">
            {[["vessel", "Vessel-wise"], ["rank", "Rank-wise"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setView(v)} className={`px-3 py-1.5 text-sm rounded ${view === v ? "bg-[#0b192c] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`req-view-${v}-btn`}>{l}</button>
            ))}
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-36 bg-white" data-testid="req-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{Object.entries(REQ_STATUS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          {(meta?.caps?.can_manage || meta?.caps?.manager) && <Button onClick={() => setOpen(true)} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="add-pipeline-requirement-btn"><Plus className="w-4 h-4 mr-1" /> New requirement</Button>}
        </div>
      </div>
      {items?.some(urgency) && <div className="text-sm text-orange-800 bg-orange-50 border border-orange-200 rounded-md px-3 py-2" data-testid="req-urgent-summary"><AlarmClock className="w-4 h-4 inline mr-1" />{items.filter(urgency).length} open requirement(s) join within 14 days or are overdue — prioritise these.</div>}
      {urgentOnly && (
        <div className="flex flex-wrap items-center gap-2" data-testid="req-urgent-filter-chip">
          <span className="text-sm font-medium text-orange-800 bg-orange-100 border border-orange-200 rounded-full px-3 py-1">Showing urgent &amp; overdue only</span>
          <button type="button" onClick={() => setParams({})} className="text-sm text-slate-600 underline hover:text-slate-900" data-testid="req-urgent-filter-clear">Show all</button>
        </div>
      )}
      {shown?.length > 0 && <GroupedTable items={shown} view={view} navigate={navigate} />}
      {(!shown || shown.length === 0) && (
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {!shown && <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>}
        {shown?.length === 0 && <div className="py-14 text-center text-sm text-slate-500" data-testid="req-empty"><ClipboardList className="w-8 h-8 text-slate-300 mx-auto mb-2" />{urgentOnly ? "No urgent or overdue requirements." : "No requirements yet."}</div>}
      </div>
      )}
      <CreateDialog open={open} onOpenChange={setOpen} meta={meta} onSaved={load} />
    </div>
  );
}
